import ApiModel from 'models/api-model';
import { isNullOrUndefined } from 'utils/object-utils';
import { NO_CLOSEST_BOOKMARKS_FOUND } from 'constants/feature/section-details-constants';

export class ContentSectionMap extends ApiModel({
  data: {
    map: {},
  },
}) {
  get map() {
    if (!isNullOrUndefined(this.data) && !isNullOrUndefined(this.data.map)) {
      return this.data.map;
    }
    return {};
  }
  processResponse({ response, isOCR = false }) {
    const map = {};
    if (isOCR) {
      response?.data?.result?.forEach((item) => {
        const {
          parentId,
          name,
          id,
          sectionId,
          revisionId,
          polygon,
          manual,
          idString,
          deleted,
          orderNum,
        } = item;
        const parsedPolygon =
          typeof polygon === 'string' ? JSON.parse(polygon) : polygon;
        const obj = {
          id,
          idString,
          name,
          polygon: parsedPolygon,
          parentId,
          revisionId,
          deleted,
          manual,
          sectionId,
          orderNum,
        };
        if (!map[sectionId]) {
          map[sectionId] = [];
        }
        map[sectionId].push(obj);
      });
    } else {
      response?.data?.result?.forEach(
        ({ parentId, name, id, is_technical, originalText }) => {
          map[id] = {
            parentId,
            name,
            id,
            isTechnical: is_technical,
            originalText,
          };
        },
      );
    }
    return {
      data: {
        map,
      },
    };
  }

  has(id) {
    return (
      !isNullOrUndefined(id) &&
      !isNullOrUndefined(this.map) &&
      !isNullOrUndefined(this.map[id])
    );
  }

  get(id) {
    if (this.has(id)) {
      return this.map[id];
    }
    return null;
  }

  getClosestBookmarkSection({ sectionId }) {
    if (this.has(sectionId)) {
      const thisContent = this.get(sectionId);
      if (!thisContent.isTechnical) {
        return thisContent;
      }
      return this.getClosestBookmarkSection({
        sectionId: thisContent.parentId,
      });
    }
    return null;
  }

  getClosestBookmarkOCRSection({ sectionId, selectedData }) {
    // Flatten selectedData.polygon to a flat array of polygons
    let selectedPolygons = [];
    if (Array.isArray(selectedData?.polygon)) {
      selectedPolygons = selectedData.polygon.flatMap((poly) =>
        Array.isArray(poly[0]) ? poly : [poly],
      );
    }

    let currentSectionId = sectionId;
    let firstCheck = true;
    while (currentSectionId != null && currentSectionId > -1) {
      const thisContent = this.get(currentSectionId);
      if (thisContent && Array.isArray(thisContent) && thisContent.length) {
        // Filter out deleted content (where deleted = 1)
        const activeContent = thisContent.filter(
          (content) => content.deleted !== 1,
        );

        if (activeContent.length > 0) {
          if (firstCheck) {
            let closestBookmark = null;
            let minDeltaY = Infinity;
            let minX = Infinity;

            activeContent.forEach((content) => {
              // Handle both single and multiple polygons
              const polygons = Array.isArray(content.polygon?.[0])
                ? content.polygon
                : [content.polygon];
              polygons.forEach((poly) => {
                for (const selPoly of selectedPolygons) {
                  for (let i = 0; i < poly.length; i += 2) {
                    const x = poly[i];
                    const y = poly[i + 1];
                    // Compare with all points in selectedPolygons
                    for (let j = 0; j < selPoly.length; j += 2) {
                      const selY = selPoly[j + 1];
                      if (y < selY) {
                        // Only above
                        const deltaY = selY - y;
                        if (
                          deltaY < minDeltaY ||
                          (deltaY === minDeltaY && x < minX)
                        ) {
                          minDeltaY = deltaY;
                          minX = x;
                          closestBookmark = content;
                        }
                      }
                    }
                  }
                }
              });
            });

            if (closestBookmark) {
              return closestBookmark;
            }
          } else {
            // For previous sections, return the item with the highest orderNum
            return activeContent.reduce((max, item) => {
              const polygons = Array.isArray(item.polygon?.[0])
                ? item.polygon
                : [item.polygon];
              // Find the largest y and x in all polygons for this item
              let itemMaxY = -Infinity,
                itemMaxX = -Infinity;
              polygons.forEach((poly) => {
                for (let i = 0; i < poly.length; i += 2) {
                  const x = poly[i];
                  const y = poly[i + 1];
                  if (y > itemMaxY || (y === itemMaxY && x > itemMaxX)) {
                    itemMaxY = y;
                    itemMaxX = x;
                  }
                }
              });

              if (!max) return item;

              const maxPolygons = Array.isArray(max.polygon?.[0])
                ? max.polygon
                : [max.polygon];
              let maxItemY = -Infinity,
                maxItemX = -Infinity;
              maxPolygons.forEach((poly) => {
                for (let i = 0; i < poly.length; i += 2) {
                  const x = poly[i];
                  const y = poly[i + 1];
                  if (y > maxItemY || (y === maxItemY && x > maxItemX)) {
                    maxItemY = y;
                    maxItemX = x;
                  }
                }
              });

              if (itemMaxY > maxItemY) return item;
              if (itemMaxY === maxItemY && itemMaxX > maxItemX) return item;
              return max;
            }, null);
          }
        }
      }
      currentSectionId -= 1;
      firstCheck = false;
    }
    return NO_CLOSEST_BOOKMARKS_FOUND;
  }

  hasSubsection({ sectionId }) {
    return (
      this.has(sectionId) &&
      Object.values(this.map).some((section) => section.parentId === sectionId)
    );
  }
}
